import { X, Space, Delete, MousePointer, Mouse, ZoomIn } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { motion } from "framer-motion";

interface KeyboardShortcutsDialogProps {
  onClose: () => void;
}

interface KeyboardKeyProps {
  children: React.ReactNode;
}

const KeyboardKey: React.FC<KeyboardKeyProps> = ({ children }) => {
  return (
    <div className="
      relative
      inline-flex items-center justify-center
      px-3 py-1.5 
      bg-gray-100 dark:bg-gray-800
      border border-gray-300 dark:border-gray-700
      rounded-md 
      shadow-sm 
      text-sm font-medium text-gray-700 dark:text-gray-300
      min-w-[32px]
      transform active:translate-y-[1px]
      after:content-['']
      after:absolute
      after:inset-0
      after:bg-gray-200 dark:after:bg-gray-700
      after:rounded-md
      after:translate-y-[-3px]
      after:-z-10
    ">
      {children}
    </div>
  );
};

export default function KeyboardShortcutsDialog({ onClose }: KeyboardShortcutsDialogProps) {
  const dialogContentRef = useClickOutside<HTMLDivElement>(onClose);
  useEscapeKey(onClose);

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }} // Added transition for overlay
    >
      <motion.div
        ref={dialogContentRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        {/* Header */}
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Keyboard Shortcuts</h2>
            <button
              onClick={onClose} // Changed to call props.onClose directly
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Navigation Section */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Basic Navigation</h3>
                <div className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey><Space className="h-4 w-4" /></KeyboardKey>
                    <span>+</span>
                    <KeyboardKey><MousePointer className="h-4 w-4" /></KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Hold spacebar and drag to pan</span>

                  <KeyboardKey><Mouse className="h-4 w-4" /></KeyboardKey>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Scroll up/down to move vertically</span>

                  <div className="flex items-center">
                    <KeyboardKey><Mouse className="h-4 w-4" /></KeyboardKey>
                    <span className="mx-2 text-gray-500">Middle</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Click and drag with middle mouse button to pan</span>

                  <div className="flex items-center">
                    <KeyboardKey><Mouse className="h-4 w-4" /></KeyboardKey>
                    <span className="mx-2 text-gray-500">Right</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Click and drag with right mouse button to pan</span>

                  <div className="flex items-center gap-2">
                    <KeyboardKey><ZoomIn className="h-4 w-4" /></KeyboardKey>
                    <span className="text-gray-500">Pinch</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Pinch to zoom on trackpad</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey><Mouse className="h-4 w-4" /></KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Hold command/control and scroll to zoom</span>
                </div>
              </div>

              {/* Project Management Section */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Project Management</h3>
                <div className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>Alt</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>N</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">New Project</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>S</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Save Project / Save Changes</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>Shift</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>S</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Save Project As...</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>O</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Open Project</span>
                </div>
              </div>

              {/* Working with Nodes Section */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Working with Nodes</h3>
                <div className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>Enter</KeyboardKey>
                  </div>
                  <div className="text-gray-700 dark:text-gray-300">
                    <div>Quick Search</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Use arrow keys to navigate, Enter to select</div>
                  </div>

                  {/* <div className="flex items-center gap-2">
                    <KeyboardKey>Shift</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey><MousePointer className="h-4 w-4" /></KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Hold Shift while clicking to select multiple nodes</span> */}

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>Shift</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Hold Shift to temporarily disable proximity connection</span>

                  <KeyboardKey><Delete className="h-4 w-4" /></KeyboardKey>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Remove a selected node or edge</span>

                  <div className="flex items-center">
                    <KeyboardKey><MousePointer className="h-4 w-4" /></KeyboardKey>
                    <span className="mx-2 text-gray-500">Drag</span>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Click and drag on empty canvas to create a selection box</span>
                </div>
              </div>

              {/* Canvas & Global Shortcuts Section */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-white mb-3">Canvas & Global Shortcuts</h3>
                <div className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-4">
                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>Z</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Undo</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>Y</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Redo</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>⌘/Ctrl</KeyboardKey>
                    <span>+</span>
                    <KeyboardKey>A</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Select All</span>
                  
                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>Esc</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Clear Selection</span>

                  <div className="flex items-center gap-2 text-gray-500">
                    <KeyboardKey>/</KeyboardKey>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300 self-center">Toggle Side Panels</span>
                </div>
              </div>
            </div>
          </motion.div>
    </motion.div>
  );
}
